import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";

import { SurveyManagementProps } from "@/features/survey/management/types";
import {
  SurveyAudience,
  SurveyStatus,
  SurveyType,
} from "@/shared/utils/constants";
import BaseButton from "@/components_v2/Button/BaseButton";
import { BUTTON_TYPES } from "@/components_v2/Button/constants";
import BaseTag from "@/components_v2/Tags/BaseTag";
import SurveyInvitesModal from "@/features/survey/management/components/SurveyInvitesModal";
import SurveyStatisticsTable from "@/features/survey/management/components/SurveyStatisticsTable";

import "./styles.css";

const SurveyManagement = ({ survey }: SurveyManagementProps) => {
  const { t } = useTranslation();

  const [openSurveyInvitesModal, setOpenSurveyInvitesModal] = useState(false);
  const surveyStatus = SurveyStatus.getItemByValue(survey.status);

  return (
    <div className="page-layout">
      <div className="page-header">
        <div className="flex gap-200">
          <BaseTag
            label={surveyStatus.label}
            style={{
              backgroundColor: surveyStatus.background,
              color: surveyStatus.color,
              fontWeight: "bold",
            }}
          />
          <h2 className="header-l-medium">{survey.title}</h2>
        </div>

        {survey.status === SurveyStatus.ACTIVE.value && (
          <BaseButton
            onClick={() => setOpenSurveyInvitesModal(true)}
            type={BUTTON_TYPES.LINK}
          >
            {survey.audience === SurveyAudience.ORGANIZATION.value
              ? t("survey.labels.inviteOrganizations")
              : t("survey.labels.inviteUsers")}
          </BaseButton>
        )}
      </div>

      <div className="flex gap-400 flex-wrap">
        <div className="flex gap-200">
          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.startDate")}</p>
            <p className="body-m-medium">
              {DateTime.fromISO(survey.startDate).toFormat("dd MMM yyyy HH:mm")}
            </p>
          </div>
          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.softDeadline")}</p>
            <p className="body-m-medium">
              {survey?.softDeadline
                ? DateTime.fromISO(survey.softDeadline).toFormat(
                    "dd MMM yyyy HH:mm",
                  )
                : t("survey.labels.noSoftDeadline")}
            </p>
          </div>
          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.hardDeadline")}</p>
            <p className="body-m-medium">
              {DateTime.fromISO(survey.hardDeadline).toFormat(
                "dd MMM yyyy HH:mm",
              )}
            </p>
          </div>
        </div>

        <div className="flex gap-200">
          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.surveyType")}</p>
            <p className="body-m-medium">
              {SurveyType.getItemByValue(survey.type).label}
            </p>
          </div>

          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.invited")}</p>
            <p className="body-m-medium">{survey.invitesCount}</p>
          </div>

          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">{t("survey.labels.responses")}</p>
            <p className="body-m-medium">{survey.responsesCount}</p>
          </div>

          <div className="survey-management-info-item flex flex-col gap-100">
            <p className="body-s-regular">
              {t("survey.labels.submittedSurveysCount")}
            </p>
            <p className="body-m-medium">{survey.submittedSurveysCount}</p>
          </div>
        </div>
      </div>

      <SurveyStatisticsTable
        defaultFilters={{ survey: survey.id }}
        softDeadline={survey.softDeadline}
        surveyStatus={survey.status}
        columnNames={[
          "name",
          "isSurveyStarted",
          "answeredQuestionsCount",
          "attachedEvidenceCount",
          "isSubmissionRequired",
          "isSubmitted",
          "submittedAt",
          "downloadReport",
          "excellenceAward",
          "actions",
        ]}
        filterNames={[
          "search",
          "isSurveyStarted",
          "isSubmissionRequired",
          "isSubmitted",
          "submissionTiming",
        ]}
      />

      <SurveyInvitesModal
        survey={survey}
        open={openSurveyInvitesModal}
        setOpen={setOpenSurveyInvitesModal}
      />
    </div>
  );
};

export default SurveyManagement;
